import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { HealthDashboard } from '@/components/monitoring/HealthDashboard'
import { IssuesTable } from '@/components/monitoring/IssuesTable'
import { PipelineLog } from '@/components/monitoring/PipelineLog'
import { SendDigestModal } from '@/components/monitoring/SendDigestModal'
import { RefreshCw, Activity, LayoutDashboard, AlertTriangle, GitBranch, Mail } from 'lucide-react'

type MonitoringTab = 'dashboard' | 'issues' | 'pipeline'

export function Monitoring() {
  const [activeTab, setActiveTab] = useState<MonitoringTab>('dashboard')
  const [showDigestModal, setShowDigestModal] = useState(false)
  const queryClient = useQueryClient()

  // Health data
  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useQuery({
    queryKey: ['monitoring-health'],
    queryFn: async () => {
      const response = await api.get('/automation/monitoring/health')
      return response.data
    },
    refetchInterval: 60000, // 1 minute
  })

  // Issues data
  const { data: issuesData, isLoading: issuesLoading, refetch: refetchIssues } = useQuery({
    queryKey: ['monitoring-issues'],
    queryFn: async () => {
      const response = await api.get('/automation/monitoring/issues?dismissed=false')
      return response.data
    },
    refetchInterval: 60000, // 1 minute
  })

  const isRefreshing = healthLoading || issuesLoading

  const handleRefresh = () => {
    refetchHealth()
    refetchIssues()
    queryClient.invalidateQueries({ queryKey: ['monitoring-issues-meta'] })
  }

  const issueCount = issuesData?.length ?? 0

  const tabs: { key: MonitoringTab; label: string; icon: React.ElementType; badge?: number }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'issues', label: 'Issues', icon: AlertTriangle, badge: issueCount > 0 ? issueCount : undefined },
    { key: 'pipeline', label: 'Pipeline', icon: GitBranch },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 flex items-center">
            <Activity className="h-7 w-7 mr-2 text-primary-600" />
            Monitoring
          </h2>
          <p className="text-secondary-600 mt-1">Scraping Operations Center</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDigestModal(true)}
            className="btn-primary flex items-center"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send Digest
          </button>
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center"
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-secondary-200">
        <nav className="flex space-x-1 -mb-px" aria-label="Monitoring tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.badge != null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <HealthDashboard
          data={healthData}
          isLoading={healthLoading}
          onKpiClick={(kpiId) => {
            if (kpiId === 'issues' || kpiId === 'ghost-units') {
              setActiveTab('issues')
            }
          }}
        />
      )}

      {activeTab === 'issues' && (
        <IssuesTable
          issues={issuesData}
          isLoading={issuesLoading}
          onRefetch={() => {
            refetchIssues()
            refetchHealth()
          }}
        />
      )}

      {activeTab === 'pipeline' && (
        <PipelineLog />
      )}

      <SendDigestModal
        isOpen={showDigestModal}
        onClose={() => setShowDigestModal(false)}
      />
    </div>
  )
}
